const AbstractVerifoneResponseRecord = require('./AbstractVerifoneResponseRecord');
const { toInt, toString } = require('@/functions/transform');

/**
 * This record represents messages received on the progress socket (for POS Client integrations only.)
 */
class VerifoneStatusRecord extends AbstractVerifoneResponseRecord {
    constructor() {
        super();

        /**
         * The name/key of this status. For our own use, this is not sent by the terminal.
         *
         * @see {VerifoneStatusNames}
         *
         * @type {string}
         */
        this.statusName = '';

        /**
         * This indicates the result code (will always be 100 for this record type).
         *
         * @type {?number}
         */
        this.result = null;

        /**
         * @type {Number}
         */
        this.statusId = null;

        this.parameters = {};

        /**
         * This field will contain any information that the POS may require that is associated with that status.
         * Each parameter will be ';' delimited and will be defined as follows: <Name>=<Value>
         *
         * @type {String}
         */
        this.rawParameters = null;

        /**
         * It's unclear in the Verifone docs if this is supposed to be shown to *our* customer (the client
         * / the operator) or the actual customer. We use it for the latter.
         *
         * @type {String}
         */
        this.customerFacingMessage = null;

        /**
         * @type {VerifoneContinueTransactionRecord[]}
         */
        this.possibleActionRecords = [];

        /**
         * Font Awesome class to go with this status.
         *
         * @type {String}
         */
        this.icon = null;
    }

    getHydrationMap() {
        return Object.assign(
            super.getHydrationMap(),
            {
                // Status ID of the transaction. All responses listed below in separate table
                statusId: {
                    field: 3,
                    transform(value) {
                        return value.toString().length > 0 ? toInt(value) : null;
                    },
                },

                // This is a text representation of the progress status
                message: {
                    field: [18, 4],
                    transform: toString,
                },

                // This field will contain any information that the POS may require that is associated with that status.
                // Each parameter will be ';' delimited and will be defined as follows: <Name>=<Value>
                rawParameters: {
                    field: 5,
                },
            }
        );
    }

    afterHydration() {
        // Split up the raw parameters (field 5) into key/value pairs.
        if (this.rawParameters && this.rawParameters.length > 0) {
            // The P400 sends XML receipt data in this field as XML=<?xml....
            // but the XML string can contain semicolons if there are html entities in it. This breaks if you
            // try to parse the parameters by splitting by semicolons.
            // The first thought was to decode those entities first then parse the string as normal here, but
            // that could break parsing the XML entirely.
            // The best solution seems to be this ugly way, and hope they never include other parameters with the XML.
            // The integration specification doc says it should only contain "XML=" so this should be safe.
            if (this.rawParameters.substr(0, 4) === 'XML=') {
                this.parameters = {
                    XML: this.rawParameters.substr(4),
                };
            } else {
                let params = this.rawParameters.split(';');
                for (let p = 0; p < params.length; p++) {
                    let param = params[p];
                    param = param.split('=');
                    this.parameters[param[0]] = param[1];
                }
            }
        }
    }

    /**
     * @param {string} actionName
     *
     * @return {VerifoneContinueTransactionRecord|undefined}
     */
    getActionRecordByName(actionName) {
        return this.possibleActionRecords.find((record) => record.actionName === actionName);
    }

    /**
     * @return {number}
     */
    getActionCount() {
        return this.possibleActionRecords.length;
    }

    /**
     * @param {string} actionName
     *
     * @return {boolean}
     */
    canPerformAction(actionName) {
        return !!this.getActionRecordByName(actionName);
    }
}

module.exports = VerifoneStatusRecord;
